import React, { useEffect, useState } from 'react'
import { Box, Typography, Tab, Tabs, styled, alpha } from '@mui/material'
import { useGetTopics } from '@/hooks/api/dashboard'
import { EgoObject, IPatternAsset, IPatternAssetPrint, SearchType, Topic } from '@/types'
import { Assets } from '@/utils/pattern-assets'
import { addUploadAssets } from '../renderer/main'
import { PrivacyType, TopicType } from '@/utils/constants'
import UpgradeCard from '@/components/UpgradeCard'
import PricingModal from '@/pages/my-profile/components/PricingModal'
import { useLocalStorage } from '@/hooks/common'

// Modern styled components with Figma-level design
const ModernPrintContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
}))

const ModernTabsContainer = styled(Box)(({ theme }) => ({
  borderBottom: '1px solid #f1f5f9',
  marginBottom: '0px', // Remove margin to eliminate gap
  padding: '0', // Ensure no padding on tabs container
}))

// Custom purple theme colors
const PURPLE_THEME = {
  primary: '#8b5cf6',
  primaryHover: '#7c3aed',
  primaryLight: '#a78bfa',
  primaryBg: '#f3f4f6',
  primaryBorder: '#e5e7eb',
  text: '#1f2937',
  textSecondary: '#6b7280',
  white: '#ffffff',
}

const ModernTabs = styled(Tabs)(({ theme }) => ({
  minHeight: '40px',
  '& .MuiTabs-indicator': {
    backgroundColor: PURPLE_THEME.primary,
    height: '2px',
    borderRadius: '1px',
  },
  '& .MuiTabs-flexContainer': {
    gap: '0px',
  },
}))

const ModernTab = styled(Tab)(({ theme }) => ({
  minHeight: '40px',
  padding: '8px 16px',
  fontSize: '14px',
  fontWeight: 500,
  textTransform: 'none',
  color: PURPLE_THEME.textSecondary,
  borderRadius: '6px 6px 0 0',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  '&.Mui-selected': {
    color: PURPLE_THEME.primary,
    fontWeight: 600,
    backgroundColor: alpha(PURPLE_THEME.primary, 0.04),
  },
  '&:hover': {
    color: PURPLE_THEME.primary,
    backgroundColor: alpha(PURPLE_THEME.primary, 0.04),
  },
}))

const PrintGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(64px, 1fr))',
  gap: '12px',
  width: '100%',
  padding: '16px', // Add padding to the content inside
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: 'repeat(auto-fill, minmax(56px, 1fr))',
    gap: '10px',
    padding: '12px',
  },
}))

const PrintItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
  width: '64px',
  height: '64px',
  borderRadius: '8px',
  border: isActive
    ? `2px solid ${PURPLE_THEME.primary}`
    : `2px solid ${PURPLE_THEME.primaryBorder}`,
  backgroundColor: PURPLE_THEME.white,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    borderColor: isActive ? PURPLE_THEME.primaryHover : PURPLE_THEME.primary,
    transform: 'translateY(-2px)',
    boxShadow: `0 4px 12px ${alpha(PURPLE_THEME.primary, 0.15)}`,
  },
  '&:active': {
    transform: 'translateY(-1px)',
  },
  '& img': {
    width: '52px',
    height: '52px',
    borderRadius: '6px',
    objectFit: 'cover',
  },
}))

const ScrollableContent = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#e2e8f0',
    borderRadius: '3px',
    '&:hover': {
      backgroundColor: '#cbd5e1',
    },
  },
}))

const EmptyState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '40px 16px',
  textAlign: 'center',
}))

const EmptyStateText = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  color: '#64748b',
  fontWeight: 500,
}))

interface Props {
  selectedId: string
  onSelect: (id: number | string) => void
  showCustomPrints: boolean
  isPremium?: boolean
  freeLimit?: number
}

export default function ModernPrintTabView({
  selectedId,
  onSelect,
  showCustomPrints = false,
  isPremium = false,
  freeLimit = 12,
}: Props) {
  const [tabIndex, setTabIndex] = useState(0)
  const getTopics = useGetTopics()
  const [topics, setTopics] = useState<Topic[]>([])
  const [pricingModalOpen, setPricingModalOpen] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [ewUser] = useLocalStorage('ewUser', null) as [any, unknown]

  useEffect(() => {
    if (showCustomPrints) loadTopics()
  }, [])

  useEffect(() => {
    if (getTopics.data && showCustomPrints) {
      const allData: Topic[] = getTopics.data || []
      let filtered = allData.filter(
        (e) => e.topicType == TopicType.CUSTOM_PRINTS.toString() && e.firstNote.egoList?.length > 0,
      )
      setTopics(filtered)
      addUploadAssets(
        filtered.map((i) => {
          const egoItem = i.firstNote.egoList[0]
          return {
            egoID: egoItem.egoID,
            egoFileURL: egoItem.photoAlbumURL,
            egoImageFullURL: egoItem.photoAlbumURL,
          }
        }),
      )
    }
  }, [getTopics.data])

  const loadTopics = (pageNo: number = 1) => {
    getTopics.mutate({
      pageNo,
      interaction: Number(PrivacyType.PRIVATE),
      topicType: Number(TopicType.CUSTOM_PRINTS),
      searchType: SearchType.loggedInUser,
    })
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue)
  }

  return (
    <ModernPrintContainer>
      {/* Hide tabs for now - only showing Standard prints */}
      {/* {showCustomPrints && (
        <ModernTabsContainer>
          <ModernTabs value={tabIndex} onChange={handleTabChange} variant='fullWidth'>
            <ModernTab label='Standard' />
            <ModernTab label='Custom' />
          </ModernTabs>
        </ModernTabsContainer>
      )} */}

      <ScrollableContent>
        {/* Always show Standard prints (tabIndex === 0) */}
        {true && (
          <PrintGrid>
            {Assets.prints.standard.map((item: IPatternAsset) => {
              const asset = item as IPatternAssetPrint
              return (
                <PrintItem
                  key={asset.id}
                  isActive={selectedId === String(asset.id)}
                  onClick={() => onSelect(asset.id)}
                >
                  <img src={`/assets/patterns/${asset.thumb}`} alt={item.name} />
                </PrintItem>
              )
            })}
          </PrintGrid>
        )}

        {/* Hide Custom prints tab content for now */}
        {/* {tabIndex === 1 && (
          <>
            {topics.length === 0 ? (
              <EmptyState>
                <EmptyStateText>No custom prints available</EmptyStateText>
                <Typography variant='caption' color='#94a3b8' sx={{ mt: 1 }}>
                  Upload your own images to create custom prints
                </Typography>
              </EmptyState>
            ) : (
              <>
                <PrintGrid
                  sx={{ paddingBottom: !isPremium && topics.length > freeLimit ? '0px' : '20px' }}
                >
                  {(!isPremium ? topics.slice(0, freeLimit) : topics).map((topic: Topic) => {
                    const { egoID, photoAlbumURL } = topic.firstNote?.egoList[0]
                    return (
                      <PrintItem
                        key={topic.topicID}
                        isActive={selectedId === `egoID-${egoID}`}
                        onClick={() => onSelect(`egoID-${egoID}`)}
                      >
                        <img src={photoAlbumURL} alt={topic.topicName} />
                      </PrintItem>
                    )
                  })}
                </PrintGrid>
                {!isPremium && topics.length > freeLimit && (
                  <Box sx={{ padding: '16px 20px 20px 20px' }}>
                    <UpgradeCard
                      variant='banner'
                      title='Unlock All Custom Prints'
                      description='Get unlimited access to all your custom uploaded prints. Create unique designs with your personal image library.'
                      itemCount={topics.length - freeLimit}
                      onUpgrade={() => setPricingModalOpen(true)}
                    />
                  </Box>
                )}
              </>
            )}
          </>
        )} */}
      </ScrollableContent>

      <PricingModal open={pricingModalOpen} onClose={() => setPricingModalOpen(false)} userId={ewUser?.userID} userEmail={ewUser?.userName} />
    </ModernPrintContainer>
  )
}
